import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Building2, Check } from 'lucide-react';

/**
 * Selector compacto del cliente activo para el Portal del Cliente Prendas.
 *
 * Props:
 *  - clientes:   Array<{ id, nombre, nit? }>
 *  - activoId:   string | null
 *  - onSelect:   (id) => void
 *  - variant:    'header' (default) | 'inline'
 *      header: tarjeta/botón grande con label "Cliente activo" (uso en dashboard)
 *      inline: chip compacto, solo el nombre (uso en headers de sub-páginas)
 */
const ClienteActivoSelector = ({ clientes = [], activoId, onSelect, variant = 'header' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!clientes || clientes.length < 2) return null;

  const activo = clientes.find((c) => c.id === activoId);

  if (variant === 'inline') {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium max-w-[200px]"
          title="Cambiar de cliente"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{activo?.nombre || 'Seleccionar'}</span>
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute right-0 top-full mt-2 w-72 max-h-72 overflow-y-auto bg-white border border-border rounded-lg shadow-lg z-30"
            >
              <p className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b">
                Cambiar de cliente
              </p>
              <ul className="py-1">
                {clientes.map((c) => {
                  const isActive = c.id === activoId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => { onSelect(c.id); setOpen(false); }}
                        className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 ${isActive ? 'bg-primary/5' : ''}`}
                      >
                        <Building2 className={`h-4 w-4 mt-0.5 shrink-0 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${isActive ? 'font-semibold text-primary' : 'text-gray-800'}`}>
                            {c.nombre}
                          </p>
                          {c.nit && (
                            <p className="text-xs text-gray-400 truncate">NIT: {c.nit}</p>
                          )}
                        </div>
                        {isActive && <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // variant: header (default)
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 max-w-[260px] text-left"
        title="Cambiar de cliente"
      >
        <div className="min-w-0">
          <p className="text-sm text-gray-500 leading-tight">Cliente activo</p>
          <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1">
            <span className="truncate">{activo?.nombre || 'Seleccionar'}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </p>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 sm:left-0 top-full mt-2 w-72 max-h-72 overflow-y-auto bg-white border border-border rounded-lg shadow-lg z-20"
          >
            <p className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b">
              Cambiar de cliente
            </p>
            <ul className="py-1">
              {clientes.map((c) => {
                const isActive = c.id === activoId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => { onSelect(c.id); setOpen(false); }}
                      className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 ${isActive ? 'bg-primary/5' : ''}`}
                    >
                      <Building2 className={`h-4 w-4 mt-0.5 shrink-0 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${isActive ? 'font-semibold text-primary' : 'text-gray-800'}`}>
                          {c.nombre}
                        </p>
                        {c.nit && (
                          <p className="text-xs text-gray-400 truncate">NIT: {c.nit}</p>
                        )}
                      </div>
                      {isActive && <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClienteActivoSelector;
